class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.grid = document.getElementById('product-grid');
    this.nextUrl = this.dataset.nextUrl;
    this.observer = null;
    this.isLoading = false;
    this.cacheKey = 'infinite_scroll_cache_' + window.location.pathname + window.location.search;
    this._onSaveStateClick = this._handleSaveStateClick.bind(this);

    this.restoreState();

    if (this.nextUrl) {
      this.initObserver();
    }
  }

  restoreState() {
    const cachedData = sessionStorage.getItem('infinite_scroll_state');
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        if (this.grid && parsed.html && parsed.url === this.cacheKey) {
          const currentItems = this.grid.querySelectorAll('.grid__item').length;
          const cachedItems = (parsed.html.match(/class="[^"]*grid__item/g) || []).length;
          
          if (cachedItems > currentItems) {
            this.grid.innerHTML = parsed.html;
            this.nextUrl = parsed.nextUrl;
            this.dataset.nextUrl = parsed.nextUrl || "";
            
            if (typeof initializeScrollAnimationTrigger === 'function') {
               initializeScrollAnimationTrigger(this.grid.querySelectorAll('.grid__item'));
            }

            const savedScroll = sessionStorage.getItem('infinite_scroll_y');
            if (savedScroll) {
               setTimeout(() => {
                 window.scrollTo({ top: parseInt(savedScroll, 10), behavior: 'instant' });
                 sessionStorage.removeItem('infinite_scroll_y');
               }, 100);
            }
          }
        }
      } catch (e) {
        console.error("Failed to parse scroll cache", e);
      }
    }

    this.bindSaveState();
  }

  bindSaveState() {
    if (!this.grid) return;
    document.addEventListener('click', this._onSaveStateClick);
  }

  _handleSaveStateClick(e) {
    const link = e.target.closest('a');
    if (!link || !link.href) return;

    if (this.grid && this.grid.contains(link)) {
      this.saveState();
    } else {
      sessionStorage.removeItem('infinite_scroll_state');
      sessionStorage.removeItem('infinite_scroll_y');
    }
  }

  saveState() {
    this.navigatingAway = true;
    if (!this.grid) return;
    const state = {
      url: this.cacheKey,
      html: this.grid.innerHTML,
      nextUrl: this.nextUrl
    };
    
    try {
      sessionStorage.setItem('infinite_scroll_state', JSON.stringify(state));
      sessionStorage.setItem('infinite_scroll_y', window.scrollY.toString());
    } catch (e) {
      // sessionStorage unavailable (quota or private mode) — silently fall back to native loading.
    }
  }

  initObserver() {
    const options = {
      root: null,
      rootMargin: '0px 0px 1200px 0px',
      threshold: 0
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isLoading && this.nextUrl) {
          this.loadNextPage();
        }
      });
    }, options);

    this.observer.observe(this);
  }

  loadNextPage() {
    if (this.isLoading) return;
    this.isLoading = true;
    this.renderLoading(true);

    fetch(this.nextUrl)
      .then(response => response.text())
      .then(responseText => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newProducts = html.querySelectorAll('#product-grid > .grid__item');
        const nextScrollElement = html.querySelector('infinite-scroll');
        
        if (newProducts.length && this.grid) {
          newProducts.forEach(product => {
            this.grid.appendChild(product);
          });
          
          if (typeof initializeScrollAnimationTrigger === 'function') {
            initializeScrollAnimationTrigger(newProducts);
          }
        }

        if (nextScrollElement && nextScrollElement.dataset.nextUrl) {
          this.nextUrl = nextScrollElement.dataset.nextUrl;
          this.dataset.nextUrl = this.nextUrl;
        } else {
          this.nextUrl = null;
          this.disconnect();
        }
      })
      .catch(e => {
        console.error('Error loading next page:', e);
      })
      .finally(() => {
        this.isLoading = false;
        this.renderLoading(false);
      });
  }

  renderLoading(isLoading) {
    if (isLoading) {
      this.innerHTML = `
        <div class="infinite-scroll-loading">
          <div class="infinite-scroll-loading__spinner"></div>
        </div>
      `;
    } else {
      this.innerHTML = '';
    }
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.innerHTML = '';
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    document.removeEventListener('click', this._onSaveStateClick);
  }
}

if (!customElements.get('infinite-scroll')) {
  customElements.define('infinite-scroll', InfiniteScroll);
}
